import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ProjectCostAnalysisItem {
  id: string;
  description: string;
  quantity: number;
  baseCost: number;
  finalCost: number;
  category: string;
  componentType: 'supplier_line_item' | 'internal_line_item' | 'assembly';
  markup: number;
  margin: number;
  // Actual cost data
  actualQuantity?: number;
  actualUnitCost?: number;
  actualTotalCost?: number;
  variance?: number;
  variancePercentage?: number;
  estimateLineItemId?: string;
}

export interface ProjectCategorySummary {
  categoryName: string;
  totalBaseCost: number;
  totalFinalCost: number;
  totalActualCost: number;
  itemCount: number;
  averageMarkup: number;
  averageMargin: number;
  variance: number;
  variancePercentage: number;
}

export interface ProjectCostAnalysisData {
  items: ProjectCostAnalysisItem[];
  categorySummaries: ProjectCategorySummary[];
  totalBaseCost: number;
  totalFinalCost: number;
  totalActualCost: number;
  overallMarkup: number;
  overallMargin: number;
  totalVariance: number;
  totalVariancePercentage: number;
}

export const useProjectCostAnalysis = (projectId: string) => {
  return useQuery<ProjectCostAnalysisData>({
    queryKey: ['project-cost-analysis', projectId],
    queryFn: async () => {
      // First, initialize actual costs with estimated values if they don't exist
      await supabase.rpc('initialize_project_actual_costs', { project_uuid: projectId });
      
      // Get project estimate
      const { data: project, error: projectError } = await supabase
        .from('projects')
        .select('estimate_id')
        .eq('id', projectId)
        .single();

      if (projectError) throw projectError;
      if (!project?.estimate_id) {
        throw new Error('No estimate associated with this project');
      }

      // Get estimate line items
      const { data: lineItems, error: lineItemsError } = await supabase
        .from('estimate_line_items')
        .select(`
          *,
          estimates!inner(id, status)
        `)
        .eq('estimate_id', project.estimate_id);

      if (lineItemsError) throw lineItemsError;

      // Get actual costs for this project
      const { data: actualCosts, error: actualCostsError } = await supabase
        .from('project_actual_costs')
        .select('*')
        .eq('project_id', projectId);

      if (actualCostsError) throw actualCostsError;

      // Create a map of actual costs by estimate line item ID
      const actualCostsMap = new Map();
      actualCosts?.forEach(cost => {
        if (cost.estimate_line_item_id) {
          actualCostsMap.set(cost.estimate_line_item_id, cost);
        }
      });

      const processedItems: ProjectCostAnalysisItem[] = [];
      const categoryTotals = new Map<string, {
        totalBaseCost: number;
        totalFinalCost: number;
        totalActualCost: number;
        itemCount: number;
        totalMarkup: number;
        totalMargin: number;
      }>();

      // Process each line item
      for (const lineItem of lineItems || []) {
        let baseCost = 0;
        let finalCost = 0;
        let category = 'Uncategorized';

        // Calculate costs based on component type
        if (lineItem.component_type === 'supplier_line_item') {
          const { data: supplierItem } = await supabase
            .from('internal_line_items')
            .select(`
              base_cost,
              category_id,
              supplier_categories!category_id(name)
            `)
            .eq('id', lineItem.component_id)
            .single();

          if (supplierItem) {
            baseCost = supplierItem.base_cost * lineItem.quantity_in_base_units;
            category = (supplierItem as any).supplier_categories?.name || 'Uncategorized';
          }
        } else if (lineItem.component_type === 'internal_line_item') {
          const { data: internalItem } = await supabase
            .from('internal_line_items')
            .select('base_cost, markup_percentage, category')
            .eq('id', lineItem.component_id)
            .single();

          if (internalItem) {
            baseCost = internalItem.base_cost * lineItem.quantity_in_base_units;
            finalCost = baseCost * (1 + (internalItem.markup_percentage || 0) / 100);
            category = internalItem.category || 'Uncategorized';
          }
        }

        // Use manual override if present
        if (lineItem.manual_override) {
          finalCost = lineItem.manual_total_cost || lineItem.total_cost_snapshot;
          baseCost = lineItem.manual_unit_cost 
            ? lineItem.manual_unit_cost * lineItem.quantity_in_base_units
            : baseCost;
        } else {
          finalCost = lineItem.total_cost_snapshot;
        }

        const markup = baseCost > 0 ? ((finalCost - baseCost) / baseCost) * 100 : 0;
        const margin = finalCost > 0 ? ((finalCost - baseCost) / finalCost) * 100 : 0;

        // Get actual costs - they should now exist due to initialization
        const actualCost = actualCostsMap.get(lineItem.id);
        const actualQuantity = actualCost?.actual_quantity ?? lineItem.quantity_in_base_units;
        const actualUnitCost = actualCost?.actual_unit_cost ?? (finalCost / lineItem.quantity_in_base_units);
        const actualTotalCost = actualCost?.actual_total_cost ?? finalCost;
        
        const variance = actualTotalCost - finalCost;
        const variancePercentage = finalCost > 0 ? (variance / finalCost) * 100 : 0;

        const item: ProjectCostAnalysisItem = {
          id: lineItem.id,
          description: lineItem.description,
          quantity: lineItem.quantity_in_base_units,
          baseCost: baseCost,
          finalCost: finalCost,
          category,
          componentType: lineItem.component_type as 'supplier_line_item' | 'internal_line_item' | 'assembly',
          markup,
          margin,
          actualQuantity,
          actualUnitCost,
          actualTotalCost,
          variance,
          variancePercentage,
          estimateLineItemId: lineItem.id
        };

        processedItems.push(item);

        // Update category totals
        if (!categoryTotals.has(category)) {
          categoryTotals.set(category, {
            totalBaseCost: 0,
            totalFinalCost: 0,
            totalActualCost: 0,
            itemCount: 0,
            totalMarkup: 0,
            totalMargin: 0
          });
        }

        const categoryTotal = categoryTotals.get(category)!;
        categoryTotal.totalBaseCost += baseCost;
        categoryTotal.totalFinalCost += finalCost;
        categoryTotal.totalActualCost += actualTotalCost;
        categoryTotal.itemCount += 1;
        categoryTotal.totalMarkup += markup;
        categoryTotal.totalMargin += margin;
      }

      // Calculate category summaries
      const categorySummaries: ProjectCategorySummary[] = Array.from(categoryTotals.entries()).map(([categoryName, totals]) => {
        const variance = totals.totalActualCost - totals.totalFinalCost;
        const variancePercentage = totals.totalFinalCost > 0 ? (variance / totals.totalFinalCost) * 100 : 0;
        
        return {
          categoryName,
          totalBaseCost: totals.totalBaseCost,
          totalFinalCost: totals.totalFinalCost,
          totalActualCost: totals.totalActualCost,
          itemCount: totals.itemCount,
          averageMarkup: totals.itemCount > 0 ? totals.totalMarkup / totals.itemCount : 0,
          averageMargin: totals.itemCount > 0 ? totals.totalMargin / totals.itemCount : 0,
          variance,
          variancePercentage
        };
      });

      // Calculate overall totals
      const totalBaseCost = processedItems.reduce((sum, item) => sum + item.baseCost, 0);
      const totalFinalCost = processedItems.reduce((sum, item) => sum + item.finalCost, 0);
      const totalActualCost = processedItems.reduce((sum, item) => sum + (item.actualTotalCost || item.finalCost), 0);
      const overallMarkup = totalBaseCost > 0 ? ((totalFinalCost - totalBaseCost) / totalBaseCost) * 100 : 0;
      const overallMargin = totalFinalCost > 0 ? ((totalFinalCost - totalBaseCost) / totalFinalCost) * 100 : 0;
      const totalVariance = totalActualCost - totalFinalCost;
      const totalVariancePercentage = totalFinalCost > 0 ? (totalVariance / totalFinalCost) * 100 : 0;

      return {
        items: processedItems,
        categorySummaries,
        totalBaseCost,
        totalFinalCost,
        totalActualCost,
        overallMarkup,
        overallMargin,
        totalVariance,
        totalVariancePercentage
      };
    },
    enabled: !!projectId
  });
};